//LIBRARIES
import React, { Component } from 'react';
import { connect } from 'react-redux';
import MediaQuery from 'react-responsive';
import { browserHistory, withRouter } from 'react-router';
import { isSubmitting, submit } from 'redux-form';
import { getTranslatedString, getCurrentLocale } from 'helpers/i18n';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import {
  StyledHeader,
  StyledMain,
  StyledHeaderContainer,
  StyledAddButton,
  StyledTableContainer,
} from 'helpers/StyledTableComponentsHelper';
import camelCase from 'lodash/camelCase';
import moment from 'moment';

//ACTIONS
import {
  getTicket,
  getAllMaintenanceTickets,
  getFilteredMaintenanceTickets,
  getTicketsStats,
  deleteTicket,
  saveTicket,
} from 'actions/maintenanceTicketActions';
import {
  createOrUpdateMaintenanceTicket,
  createOrUpdateMaintenanceTask,
  deleteTask,
  getAssets,
  getMaintenanceCategories,
  getMaintenanceLocations,
} from 'actions/maintenanceActions';
import { getProperties, getUnits } from 'actions/landlordPropertiesActions';
import { getGroupedVendors } from 'actions/vendorsActions';
import { getCompanyPreferences } from 'actions/companyDetailsActions';

//COMPONENTS
import AddAndViewMaintenanceTickets from 'components/landlord_app/maintenance/components/AddAndViewMaintenanceTickets';
import HeaderWithPeriod from 'components/shared/HeaderWithPeriod';
import TabbedTable from 'components/ui/TabbedTable';
import MobileTable from 'components/ui/MobileTable';
import TableFilters from 'components/ui/TableFilters';
import PriorityIcon from 'components/ui/PriorityIcon';
import StatusTag from 'components/ui/StatusTag';
import AntDrawer from 'components/ui/Drawer';
import { RenderIfToggleOn, RenderIfToggleOff } from 'components/shared/ToggleRenderer';
import TicketCreating from './TicketCreating';
import TicketViewing from './TicketViewing/TicketViewing';

//HELPERS
import { AccentColor } from 'helpers/AccentColorHelper';
import { featureEnabled } from 'helpers/ToggleBot';
import { maintenanceTicketStatuses } from 'helpers/constants/Statuses';

const priorities = ['Normal', 'High', 'Emergency'];
const requestTypes = [
  'Tenant Request - Walk in',
  'Tenant Request - Call',
  'Tenant Request - Email',
  'Tenant Request - Other',
  'Preventative Maintenance',
  'Kiosk Request',
  'Inspection',
  'Maintenance',
  'Routine',
  'Portal Request - Maintenance',
  'Portal Request - Other',
];

class TicketsPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tableData: [],
      createTicketDrawerOpen: false,
      viewTicketDrawerOpen: false,
      filtersActive: false,
      filters: {
        search: '',
        properties: Array(this.props.properties.length).fill(false),
        statuses: Array(this.statuses().length).fill(false),
        priorities: Array(priorities.length).fill(false),
        requestTypes: Array(requestTypes.length).fill(false),
      },
      filterFormTouched: false,
      drawerVisible: false,
      currentTab: 'needs_action',
      sortKey: '',
      sortOrder: '',
      offset: 0,
    };
  }

  setRef = (ref) => (this.table = ref);

  fetchTicketsAndStats = () => {
    this.props.getTicketsStats();
    this.fetchTickets();
  };

  componentDidMount = () => {
    const { ticketID } = this.props.routeParams;
    this.fetchTicketsAndStats();
    this.props.getProperties({ for_multiselect_single_unit: true });
    this.props.getCompanyPreferences();

    if (ticketID) {
      this.openTicketViewDrawer(ticketID);
    }
  };

  toggleOldTicketViewDrawer = async (maintenanceTicketID) => {
    if (!this.state.viewTicketDrawerOpen && maintenanceTicketID) {
      await this.props.getTicket(maintenanceTicketID);

      if (!this.props.ticketDetails || !this.props.ticketDetails.id) {
        return;
      }

      let assetParams = {
        property_id: this.props.ticketDetails.property_id,
        for_multiselect: true,
      };

      const unitId = this.props.ticketDetails.unit_id;

      if (unitId) {
        assetParams = { ...assetParams, unit_id: unitId };
      }

      this.props.getAssets(assetParams);
      this.props.getUnits(this.props.ticketDetails.property_id, {
        for_multiselect: true,
      });
      this.props.getMaintenanceCategories(this.props.ticketDetails.property_id);
      this.props.getMaintenanceLocations(this.props.ticketDetails.property_id);
      this.props.getGroupedVendors();
      this.setState({
        viewTicketDrawerOpen: true,
      });
      browserHistory.push(`/companies/maintenance/tickets/${maintenanceTicketID}`);
      this.props.getGroupedVendors();
      return;
    }

    this.setState({
      viewTicketDrawerOpen: false,
    });
    browserHistory.push('/companies/maintenance/tickets');
  };

  openTicketViewDrawer = async (ticketID) => {
    if (!featureEnabled('ui.maintenance.new_tickets_ui')) {
      this.toggleOldTicketViewDrawer(ticketID);
      return;
    }
    browserHistory.push(`/companies/maintenance/tickets/${ticketID}`);

    await this.props.getTicket(ticketID);
    this.setState({ viewTicketDrawerOpen: true });
  };

  addTicket = async (formData) => {
    await this.props.createOrUpdateMaintenanceTicket(formData);
    this.setState(
      {
        offset: 0,
      },
      () => this.fetchTicketsAndStats()
    );
    this.toggleCreateTicketDrawer();
  };

  reloadTable = () => {
    this.setState(
      {
        offset: 0,
      },
      () => this.fetchTicketsAndStats()
    );
  };

  reloadTableAndSelectedTicket = () => {
    this.setState(
      {
        offset: 0,
      },
      () => this.fetchTicketsAndStats()
    );
    return this.props.getTicket(this.props.ticketDetails.id);
  };

  updateTicket = async (formData, ticketID = this.props.ticketDetails.id) => {
    await this.props.createOrUpdateMaintenanceTicket(formData, ticketID);
    this.setState(
      {
        offset: 0,
      },
      () => this.fetchTicketsAndStats()
    );
    this.props.getTicket(ticketID);
  };

  deleteTicket = async (cancellationReason) => {
    await this.props.deleteTicket(this.props.ticketDetails.id, cancellationReason);
    this.toggleOldTicketViewDrawer();
    this.setState(
      {
        offset: 0,
      },
      () => this.fetchTicketsAndStats()
    );
  };

  createOrUpdateTask = async (task, taskID) => {
    await this.props.createOrUpdateMaintenanceTask(task, taskID, this.props.ticketDetails.id);
    this.setState(
      {
        offset: 0,
      },
      () => this.fetchTicketsAndStats()
    );
  };

  deleteTask = async (taskId) => {
    await this.props.deleteTask(taskId, this.props.ticketDetails.id);
    this.setState(
      {
        offset: 0,
      },
      () => this.fetchTicketsAndStats()
    );
  };

  statuses = () => {
    let statuses = [
      'new',
      'Reviewed',
      'Fixed',
      'Contact Tenant',
      'Closed',
      'Acknowledged',
      'cancelled',
      'cancelled_dup',
    ];

    if (featureEnabled('ui.maintenance.tickets.show_cancel_no_response')) {
      statuses.push('cancelled_no_res');
    }

    if (!featureEnabled('ui.create_service_request.disable_working_statuses')) {
      statuses.push(
        'Scheduled',
        'Fixing',
        'Parts Needed',
        'Contractor Needed',
        'Deferred',
        'auto_deferred'
      );
    }

    if (featureEnabled('ui.create_service_request.assign_status')) {
      statuses.push('assigned');
    }

    if (featureEnabled('maintenance.vendor_statuses_and_notifications')) {
      statuses.push('vendor_ongoing', 'vendor_other', 'vendor_cancelled');
    }

    if (featureEnabled('maintenance.maintenance_dispatch')) {
      statuses.push(
        maintenanceTicketStatuses.PROPOSED_SCHEDULED_DATE,
        maintenanceTicketStatuses.CONTRACTOR_NEEDED_REVIEW,
        maintenanceTicketStatuses.PARTS_NEEDED_REVIEW
      );
    }

    return statuses;
  };

  filterProperties = (booleanArrayMask) => {
    return this.props.properties.filter((_, i) => booleanArrayMask[i]);
  };

  filterStatuses = (booleanArrayMask) => {
    return this.statuses().filter((_, i) => booleanArrayMask[i]);
  };

  filterPriorities = (booleanArrayMask) => {
    return priorities.filter((_, i) => booleanArrayMask[i]);
  };

  filterRequestTypes = (booleanArrayMask) => {
    return requestTypes.filter((_, i) => booleanArrayMask[i]);
  };

  handleClearSearch = (setFieldValue) => {
    this.setState(
      {
        filtersActive: false,
        filters: {
          search: '',
          properties: Array(this.props.properties.length).fill(false),
          statuses: Array(this.statuses().length).fill(false),
          priorities: Array(priorities.length).fill(false),
          requestTypes: Array(requestTypes.length).fill(false),
        },
        offset: 0,
      },
      () => this.fetchTickets()
    );

    if (this.table) this.table.scrollToTop(0);
    setFieldValue('search', '');
    setFieldValue('properties', Array(this.props.properties.length).fill(false));
    setFieldValue('statuses', Array(this.statuses().length).fill(false));
    setFieldValue('priorities', Array(priorities.length).fill(false));
    setFieldValue('requestTypes', Array(requestTypes.length).fill(false));
  };

  handleFilters = (values) => {
    const isFiltersUntouched =
      values.search === '' &&
      !values.properties.includes(true) &&
      !values.statuses.includes(true) &&
      !values.priorities.includes(true) &&
      !values.requestTypes.includes(true);
    const newState = {
      filtersActive: !isFiltersUntouched,
      filters: {
        search: values.search,
        properties: values.properties,
        statuses: values.statuses,
        priorities: values.priorities,
        requestTypes: values.requestTypes,
      },
      drawerVisible: false,
      offset: 0,
    };

    this.setState(newState, () => this.fetchTickets());
    if (this.table) this.table.scrollToTop(0);
  };

  handleOpenDrawer = () => {
    this.setState({
      drawerVisible: true,
    });
  };

  handleDrawerClose = () => {
    this.setState({
      drawerVisible: false,
    });
  };

  handleTabChange = (tabName) => {
    this.setState(
      {
        currentTab: tabName,
        offset: 0,
      },
      () => {
        this.fetchTickets();
      }
    );
    if (this.table) this.table.scrollToTop(0);
  };

  columns = [
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.priority'),
      dataKey: 'priority',
      sortable: false,
      width: 125,
      render: ({ data }) => <PriorityIcon priority={data} />,
    },
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.ticketNo'),
      dataKey: 'ticket_number',
      sortable: true,
      width: 125,
    },
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.status'),
      dataKey: 'status',
      sortable: false,
      width: 250,
      render: ({ data }) => <StatusTag status={data} />,
    },
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.property'),
      dataKey: featureEnabled('ui.properties.namespace_by_city')
        ? 'property_name_city'
        : 'property_name',
      sortable: true,
      width: 300,
    },
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.unit'),
      dataKey: 'unit_number',
      sortable: true,
      width: 150,
    },
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.subject'),
      dataKey: 'subject',
      sortable: true,
      width: 400,
    },
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.tasks'),
      dataKey: 'tasks',
      sortable: false,
      width: 150,
      render: ({ data }) => `${data.filter((task) => task.complete).length}/${data.length}`,
    },
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.requestType'),
      dataKey: 'request_type',
      sortable: false,
      width: 300,
      render: ({ data }) => {
        if (!data) return '-';
        data = data.replace(/tenant/gi, 'Resident');
        return getTranslatedString(`landlordPortal.landlordMaintenance.${camelCase(data)}`);
      },
    },
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.scheduledDate'),
      dataKey: 'scheduled_starts_at',
      sortable: true,
      width: 200,
      hide: !featureEnabled('maintenance.scheduling'),
      render: ({ data }) => {
        const date = moment(data);
        if (date.isValid())
          return date.locale(getCurrentLocale() || 'en').format('MMMM DD, hh:mm A');
        return getTranslatedString('landlordPortal.landlordMaintenance.notSchedule');
      },
    },
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.submittedDate'),
      dataKey: 'request_date',
      sortable: true,
      width: 200,
      render: ({ data }) => {
        const date = moment(data);
        if (date.isValid())
          return date.locale(getCurrentLocale() || 'en').format('MMMM DD, hh:mm A');
        return '-';
      },
    },
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.statusModified'),
      dataKey: 'status_last_modified',
      width: 200,
      sortable: true,
      render: ({ data }) => {
        const date = moment(data);
        if (data && date.isValid())
          return date.locale(getCurrentLocale() || 'en').format('MMMM DD, hh:mm A');
        return '-';
      },
    },
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.modifiedLast'),
      dataKey: 'last_updated',
      sortable: true,
      width: 200,
      render: ({ data }) => {
        const date = moment(data);
        if (date.isValid())
          return date.locale(getCurrentLocale() || 'en').format('MMMM DD, hh:mm A');
        return '-';
      },
    },
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.assignee'),
      dataKey: 'assigned_to',
      sortable: true,
      width: 200,
      render: ({ data }) => {
        if (!data) {
          return getTranslatedString('landlordPortal.landlordMaintenance.notAssigned');
        }
        return data;
      },
    },
  ];

  accentColor = AccentColor();

  handleSort = (key, order) => {
    this.setState(
      {
        sortKey: key,
        sortOrder: order,
        offset: 0,
      },
      () => {
        this.fetchTickets();
      }
    );
    if (this.table) this.table.scrollToTop(0);
  };

  fetchTickets = () => {
    const { currentTab, filters, filtersActive, offset, sortKey, sortOrder } = this.state;

    let fetchPromise = null;
    let filterOptions = {
      sort_key: sortKey,
      sort_order: sortOrder,
    };

    let subset = currentTab;

    if (filtersActive) {
      const propertiesToFilter = this.filterProperties(filters.properties).map(
        (data) => data.value
      );
      const statusesToFilter = this.filterStatuses(filters.statuses);
      const prioritiesToFilter = this.filterPriorities(filters.priorities);
      const requestTypesToFilter = this.filterRequestTypes(filters.requestTypes);

      filterOptions = {
        query: filters.search,
        property_ids: propertiesToFilter,
        statuses: statusesToFilter,
        priorities: prioritiesToFilter,
        request_types: requestTypesToFilter,
        sort_key: sortKey,
        sort_order: sortOrder,
      };
      subset = 'all';
    }

    fetchPromise = this.props.getFilteredMaintenanceTickets(subset, offset, filterOptions);

    fetchPromise.then(() => {
      this.setState({
        offset: this.props.maintenanceTickets.length,
      });
    });
  };

  toggleCreateTicketDrawer = () => {
    this.setState((prevState) => {
      return {
        ...prevState,
        createTicketDrawerOpen: !prevState.createTicketDrawerOpen,
      };
    });
  };

  render() {
    const { filters } = this.state;
    const { maintenanceTickets, maintenanceTicketsCounts, searchTotalResultsCount } = this.props;
    const properties = this.props.properties.map((data) => data.label);
    const filterOptions = [
      {
        useSearch: true,
        options: properties,
        name: 'properties',
        title: getTranslatedString('landlordPortal.landlordMaintenance.selectPropertyFilterBy'),
      },
      {
        useSearch: false,
        options: this.statuses().map((statusCode) => {
          if (this.props.propertyTypes !== 'commercial') {
            statusCode = statusCode.replace(/tenant/gi, 'Resident');
          }
          return getTranslatedString(`maintenanceStatus.${camelCase(statusCode)}`);
        }),
        name: 'statuses',
        title: getTranslatedString('landlordPortal.landlordMaintenance.filterTicketStatus'),
      },
      {
        useSearch: false,
        options: priorities.map((priorityCode) => {
          if (this.props.propertyTypes === 'commercial') {
            priorityCode = priorityCode.replace(/tenant/gi, 'Resident');
          }
          return getTranslatedString(
            `landlordPortal.landlordMaintenance.priorities.${camelCase(priorityCode)}`
          );
        }),
        name: 'priorities',
        title: getTranslatedString('landlordPortal.landlordMaintenance.filterTicketPriority'),
      },
      {
        useSearch: false,
        options: requestTypes.map((requestTypeCode) => {
          if (this.props.propertyTypes === 'commercial') {
            requestTypeCode = requestTypeCode.replace(/tenant/gi, 'Resident');
          }
          return getTranslatedString(
            `landlordPortal.landlordMaintenance.${camelCase(requestTypeCode)}`
          );
        }),
        name: 'requestTypes',
        title: getTranslatedString('landlordPortal.landlordMaintenance.filterTicketRequestType'),
      },
    ];
    const tabs = [
      {
        title: getTranslatedString('landlordPortal.landlordMaintenance.needsAction'),
        label: getTranslatedString('landlordPortal.landlordMaintenance.needsAction'),
        value: 'needs_action',
        count: maintenanceTicketsCounts['needs_action'] || 0,
        onClick: () => this.handleTabChange('needs_action'),
      },
      {
        title: getTranslatedString('landlordPortal.landlordMaintenance.assignedToMe'),
        label: getTranslatedString('landlordPortal.landlordMaintenance.assignedToMe'),
        value: 'assigned_to_me',
        count: maintenanceTicketsCounts['assigned_to_me'] || 0,
        onClick: () => this.handleTabChange('assigned_to_me'),
      },
      {
        title: getTranslatedString('landlordPortal.landlordMaintenance.urgent'),
        label: getTranslatedString('landlordPortal.landlordMaintenance.urgent'),
        value: 'urgent',
        count: maintenanceTicketsCounts['urgent'] || 0,
        onClick: () => this.handleTabChange('urgent'),
      },
      {
        title: getTranslatedString('landlordPortal.landlordMaintenance.unassigned'),
        label: getTranslatedString('landlordPortal.landlordMaintenance.unassigned'),
        value: 'unassigned',
        count: maintenanceTicketsCounts['unassigned'] || 0,
        onClick: () => this.handleTabChange('unassigned'),
        hidden: featureEnabled('ui.create_service_request.disable_working_statuses'),
      },
      {
        title: getTranslatedString('landlordPortal.landlordMaintenance.inProgress'),
        label: getTranslatedString('landlordPortal.landlordMaintenance.inProgress'),
        value: 'in_progress',
        count: maintenanceTicketsCounts['in_progress'] || 0,
        onClick: () => this.handleTabChange('in_progress'),
      },
      {
        title: getTranslatedString('landlordPortal.landlordMaintenance.fixed'),
        label: getTranslatedString('landlordPortal.landlordMaintenance.fixed'),
        value: 'fixed',
        count: maintenanceTicketsCounts['fixed'] || 0,
        onClick: () => this.handleTabChange('fixed'),
      },
      {
        title: getTranslatedString('landlordPortal.landlordMaintenance.closed'),
        label: getTranslatedString('landlordPortal.landlordMaintenance.closed'),
        value: 'closed',
        count: maintenanceTicketsCounts['closed'] || 0,
        onClick: () => this.handleTabChange('closed'),
      },
      {
        title: getTranslatedString('landlordPortal.landlordMaintenance.scheduled'),
        label: getTranslatedString('landlordPortal.landlordMaintenance.scheduled'),
        value: 'scheduled',
        count: maintenanceTicketsCounts['scheduled'] || 0,
        onClick: () => this.handleTabChange('scheduled'),
        hidden: !featureEnabled('maintenance.scheduling'),
      },
      {
        title: getTranslatedString('landlordPortal.landlordMaintenance.allOpen'),
        label: getTranslatedString('landlordPortal.landlordMaintenance.allOpen'),
        value: 'all_open',
        count: maintenanceTicketsCounts['all_open'] || 0,
        onClick: () => this.handleTabChange('all_open'),
      },
      {
        title: getTranslatedString('landlordPortal.landlordMaintenance.cancelled'),
        label: getTranslatedString('landlordPortal.landlordMaintenance.cancelled'),
        value: 'cancelled',
        count: maintenanceTicketsCounts['cancelled'] || 0,
        onClick: () => this.handleTabChange('cancelled'),
      },
    ];

    return (
      <StyledMain>
        <StyledHeaderContainer>
          <StyledHeader>
            <HeaderWithPeriod
              headerText={getTranslatedString('landlordPortal.landlordMaintenance.tickets')}
              newStyle
            />
            <StyledAddButton accentColor={this.accentColor} onClick={this.toggleCreateTicketDrawer}>
              {getTranslatedString('landlordPortal.landlordMaintenance.createNew')}
            </StyledAddButton>
          </StyledHeader>
          <TableFilters
            filters={filters}
            handleSubmitFilters={this.handleFilters}
            handleClearFilters={this.handleClearSearch}
            filterOptions={filterOptions}
            accentColor={this.accentColor}
          />
        </StyledHeaderContainer>

        <StyledTableContainer>
          {this._renderTableView({ maintenanceTickets, tabs, searchTotalResultsCount })}
          {this._renderMobileView({
            maintenanceTickets,
            tabs,
            currentTab: this.state.currentTab,
            tabChange: this.handleTabChange,
          })}
        </StyledTableContainer>

        {this._renderOldAddAndViewTicketDialogs()}
        {this._renderCreateTicketDrawer()}
        {this._renderViewTicketDrawer()}
      </StyledMain>
    );
  }

  _renderOldAddAndViewTicketDialogs = () => (
    <RenderIfToggleOff toggle="ui.maintenance.new_tickets_ui">
      <AddAndViewMaintenanceTickets
        addTicket={this.addTicket}
        fullScreen={this.props.fullScreen}
        drawerIsOpen={this.state.createTicketDrawerOpen}
        toggleAddTicket={this.toggleCreateTicketDrawer}
        showDrawer={this.state.viewTicketDrawerOpen}
        toggleDrawer={this.toggleOldTicketViewDrawer}
        updateTicket={this.updateTicket}
        deleteAction={this.deleteTicket}
        createOrUpdateTask={this.createOrUpdateTask}
        deleteTask={this.deleteTask}
        maintenanceTicket={this.props.ticketDetails}
        submitMaintenanceTicket={() => this.props.submit('add_ticket_form')}
        submittingMaintenance={this.props.submitting}
        companyPreferences={this.props.companyPreferences}
      />
    </RenderIfToggleOff>
  );

  _renderCreateTicketDrawer = () => (
    <RenderIfToggleOn toggle="ui.maintenance.new_tickets_ui">
      <AntDrawer
        maskClosable={false}
        keyboard
        visible={this.state.createTicketDrawerOpen}
        onClose={this.toggleCreateTicketDrawer}
        closable={false}
        width={600}
      >
        {this.state.createTicketDrawerOpen && (
          <TicketCreating
            handleClose={this.toggleCreateTicketDrawer}
            onSubmissionComplete={this.reloadTable}
          />
        )}
      </AntDrawer>
    </RenderIfToggleOn>
  );

  _renderViewTicketDrawer = () => (
    <RenderIfToggleOn toggle="ui.maintenance.new_tickets_ui">
      <AntDrawer
        maskClosable={false}
        keyboard
        visible={this.state.viewTicketDrawerOpen}
        onClose={this.toggleOldTicketViewDrawer}
        closable={false}
        width={600}
      >
        {this.state.viewTicketDrawerOpen && (
          <TicketViewing
            handleClose={this.toggleOldTicketViewDrawer}
            selectedTicket={this.props.ticketDetails}
            onSubmissionComplete={async () => await this.reloadTable()}
          />
        )}
      </AntDrawer>
    </RenderIfToggleOn>
  );

  _renderTableView = ({ maintenanceTickets, tabs, searchTotalResultsCount }) => {
    return (
      <MediaQuery query="(min-device-width: 750px)">
        <TabbedTable
          setRef={this.setRef}
          accentColor={this.accentColor}
          tabs={tabs}
          columns={this.columns}
          data={maintenanceTickets}
          loadMoreData={this.fetchTickets}
          isLoading={this.props.fetchingMaintenanceTickets}
          filtersActive={this.state.filtersActive}
          onRowClick={(data) => {
            this.openTicketViewDrawer(data.original_id);
          }}
          onColumnClick={(data) => {
            this.handleSort(data.key, data.order);
          }}
          searchTotalResultsCount={searchTotalResultsCount}
        />
      </MediaQuery>
    );
  };

  _renderMobileView = ({ maintenanceTickets, tabs, currentTab, tabChange }) => {
    let groupBy = 'property_name';
    if (featureEnabled('ui.properties.namespace_by_city')) {
      groupBy = 'property_name_city';
    }
    return (
      <MediaQuery query="(max-device-width: 750px)">
        <MobileTable
          groupRowsBy={groupBy}
          data={maintenanceTickets}
          tabs={tabs}
          tabIndex={currentTab}
          tabChange={tabChange}
          filtersActive={this.state.filtersActive}
          title="subject"
          tag="status"
          stickyHeaderTop={this.state.filters.search ? '137px' : '107px'}
          rowHeader="ticket_number"
          isLoading={this.props.fetchingMaintenanceTickets}
          fieldsAboveFold={[
            {
              title: getTranslatedString('landlordPortal.landlordMaintenance.unitNo'),
              key: 'unit_number',
            },
            {
              title: getTranslatedString('landlordPortal.landlordMaintenance.priority'),
              key: 'priority',
            },
          ]}
          fieldsBelowFold={[
            {
              title: getTranslatedString('landlordPortal.landlordMaintenance.requestType'),
              key: 'request_type',
            },
            {
              title: getTranslatedString('landlordPortal.landlordMaintenance.scheduledDate'),
              key: 'scheduled_starts_at',
              hide: !featureEnabled('maintenance.scheduling'),
            },
            {
              title: getTranslatedString('landlordPortal.landlordMaintenance.submittedDate'),
              key: 'request_date',
            },
            {
              title: getTranslatedString('landlordPortal.landlordMaintenance.lastUpdated'),
              key: 'last_updated',
            },
            {
              title: getTranslatedString('landlordPortal.landlordMaintenance.assignee'),
              key: 'assigned_to',
            },
          ]}
          onRowClick={(data) => {
            this.openTicketViewDrawer(data.id);
          }}
        />
      </MediaQuery>
    );
  };
}

const mapStateToProps = (store) => ({
  maintenanceTickets: store.maintenanceTicket.maintenanceTickets,
  fetchingMaintenanceTickets: store.maintenanceTicket.fetchingMaintenanceTickets,
  maintenanceTicketsCounts: store.maintenanceTicket.maintenanceTicketsCounts,
  ticketDetails: store.maintenanceTicket.ticketDetails,
  properties: store.landlordProperties.properties,
  submitting: isSubmitting('add_ticket_form')(store),
  companyPreferences: store.companyDetails.companyPreferences,
  propertyTypes: store.userTypes.propertyTypes,
  searchTotalResultsCount: store.maintenanceTicket.searchTotalResultsCount,
});

const actionCreators = {
  getAllMaintenanceTickets,
  getFilteredMaintenanceTickets,
  getTicketsStats,
  createOrUpdateMaintenanceTicket,
  getProperties,
  submit,
  getTicket,
  saveTicket,
  getUnits,
  getGroupedVendors,
  deleteTicket,
  createOrUpdateMaintenanceTask,
  deleteTask,
  getAssets,
  getCompanyPreferences,
  getMaintenanceCategories,
  getMaintenanceLocations,
};

export default withMobileDialog()(
  withRouter(connect(mapStateToProps, actionCreators)(TicketsPage))
);
